import { Dispatch, SetStateAction } from "react"

// ** Third Party Components
import { Loader } from "lucide-react"
import { TickCircle } from "iconsax-reactjs"
import { useNavigate, useLocation } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { queryClient } from "@pma/utils/constants"
import { LOGIN_COMPLETE_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Services
import { switchWorkspaceService } from "@pma/services/common"

// ** Theme Components
import { Separator } from "@pma/components/common/separator"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@pma/components/common/dialog"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

interface WorkSpaceDialogProps {
  workSpaceDialog: boolean
  setWorkSpaceDialog: Dispatch<SetStateAction<boolean>>
}

const WorkSpaceDialog = ({ workSpaceDialog, setWorkSpaceDialog }: WorkSpaceDialogProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig, signIn } = useAuth()

  const availableWorkspaces = authConfig?.companies_of_user
  const currentOrganizationId = authConfig?.last_organization_id

  const switchWorkspaceMutation = useMutation({
    mutationKey: ["switch-workspace"],
    mutationFn: (id: string) => switchWorkspaceService({ last_organization_id: id }),
    onSuccess: (data: DictType) => {
      signIn({ ...data })
      toast({
        title: translate("Success"),
        description: translate("Switching workspace"),
        variant: "success",
      })

      setWorkSpaceDialog(false)
      queryClient.clear()

      navigate(LOGIN_COMPLETE_PAGE_ROUTE, { state: { from: location.pathname } })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to switch workspace at this time"),
        variant: "error",
      })
    },
  })

  return (
    <Dialog open={workSpaceDialog} onOpenChange={setWorkSpaceDialog}>
      <DialogContent className={cn("rounded-[20px]", isHebrew ? "rtl" : "ltr")}>
        <DialogHeader>
          <DialogTitle>{translate("Workspaces")}</DialogTitle>
        </DialogHeader>
        <Separator className='m-0 w-full' />
        <DialogDescription className='max-h-72 overflow-y-auto'>
          <div className='flex flex-col gap-4 md:p-4'>
            {availableWorkspaces?.length > 0 ? (
              availableWorkspaces.map((workSpace: any, index: number) => (
                <div className='flex flex-col' key={index}>
                  <div
                    className={cn(
                      "flex flex-col gap-4 rounded-lg p-4 shadow",
                      currentOrganizationId === workSpace.id
                        ? "border-2 border-indigo-500 bg-indigo-50"
                        : "border border-[#E5E5EC]",
                    )}
                  >
                    <div className='flex gap-4'>
                      <img
                        src={
                          workSpace?.company_logo?.url || `${process.env.PUBLIC_URL}/group-232.svg`
                        }
                        alt='Organization logo'
                        className='h-[80px] w-[80px] rounded-lg bg-indigo-600'
                      />
                      <div className='flex w-full gap-3 p-2'>
                        <div className='flex w-full flex-col gap-1'>
                          <p className='font-[inter] text-base font-medium text-[#1F2633]'>
                            {workSpace?.organization_name}
                          </p>
                          <div className='flex justify-between'>
                            <div className='flex items-center gap-2'>
                              <img
                                loading='lazy'
                                src={`${workSpace?.company_logo?.user_picture?.url || process.env.PUBLIC_URL + "/user-profile.svg"}`}
                                className='h-6 w-6 rounded-full'
                              />
                              <p className='font-[inter] text-sm font-normal text-[#1F2633]'>
                                {workSpace?.role?.name || "---"}
                              </p>
                            </div>
                            <button
                              className='flex h-8 w-20 items-center justify-center rounded-[20px] px-4 py-2 text-white shadow'
                              style={{
                                background:
                                  "linear-gradient(0deg, var(--indigo-600, #444CE7), var(--indigo-600, #444CE7)),linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)",
                              }}
                              onClick={() => {
                                if (currentOrganizationId !== workSpace.id)
                                  switchWorkspaceMutation.mutate(workSpace.id)
                              }}
                              disabled={switchWorkspaceMutation.isPending}
                            >
                              {switchWorkspaceMutation.isPending ? (
                                <Loader className='animate-spin text-white' />
                              ) : currentOrganizationId === workSpace.id ? (
                                <TickCircle size={16} />
                              ) : (
                                translate("Switch")
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className=''>{translate("No workspaces found")}</div>
            )}
          </div>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}

export default WorkSpaceDialog
